import React, { useState, useEffect, useRef, useId } from 'react';
import { getClientHistory, deleteStopPayment, deleteClientVisit, updateStopPayment, addManualVisit } from './api';
import { useTranslation } from '../i18n';

const historyCSS = `
  .ms-history-savebtn:hover:not(:disabled),
  .ms-history-addbtn:hover,
  .ms-history-smallpay:hover:not(:disabled) { background: #0f4d2c !important; }
`;

function getLocalDate() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export default function ClientHistory({ client, onClose }) {
  const { t, lang } = useTranslation();
  const titleId = useId();
  const addTitleId = useId();
  const visitDateId = useId();
  const chargedId = useId();
  const paidId = useId();
  const tipId = useId();
  const notesId = useId();
  const completedId = useId();
  const paidCheckId = useId();

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [markingPaid, setMarkingPaid] = useState(null);
  const [markingComplete, setMarkingComplete] = useState(null);
  const [showAddVisit, setShowAddVisit] = useState(false);
  const [addingVisit, setAddingVisit] = useState(false);
  const [visitForm, setVisitForm] = useState({
    visit_date: getLocalDate(),
    amount_charged: client.recurring_amount || '',
    amount_paid: '',
    tip: '',
    payment_notes: '',
    completed: true,
    paid: false
  });

  const modalRef = useRef(null);
  const closeBtnRef = useRef(null);
  const triggerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => { loadHistory(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus management: capture previously-focused element, focus close button, restore on unmount.
  useEffect(() => {
    triggerRef.current = document.activeElement;
    if (closeBtnRef.current && typeof closeBtnRef.current.focus === 'function') {
      closeBtnRef.current.focus();
    }
    return () => {
      mountedRef.current = false;
      const trigger = triggerRef.current;
      if (trigger && typeof trigger.focus === 'function') {
        try { trigger.focus(); } catch (e) { /* ignore */ }
      }
    };
  }, []);

  // Escape closes the modal.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (onClose) onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const loadHistory = async () => {
    try {
      const { data } = await getClientHistory(client.id);
      if (mountedRef.current) setHistory(data);
    } catch {
      if (mountedRef.current) setHistory([]);
    }
    if (mountedRef.current) setLoading(false);
  };

  const handleMarkPaid = async (visit) => {
    setMarkingPaid(visit.id);
    try {
      await updateStopPayment(visit.id, {
        paid: true,
        completed: true,
        amount_paid: visit.amount_charged || 0
      });
      setHistory(history.map(h => h.id === visit.id ? {
        ...h, paid: true, completed: true, amount_paid: h.amount_charged
      } : h));
    } catch {
      alert(t('history.err_mark_paid'));
    }
    setMarkingPaid(null);
  };

  const handleToggleComplete = async (visit) => {
    setMarkingComplete(visit.id);
    try {
      await updateStopPayment(visit.id, { completed: !visit.completed });
      setHistory(history.map(h => h.id === visit.id ? {
        ...h, completed: !h.completed
      } : h));
    } catch {
      alert(t('history.err_update_visit'));
    }
    setMarkingComplete(null);
  };

  const handleDeletePayment = async (visitId) => {
    if (!window.confirm(t('history.confirm_clear_payment'))) return;
    try {
      const { data } = await deleteStopPayment(visitId);
      setHistory(history.map(h => h.id === visitId ? { ...h, ...data } : h));
    } catch {
      alert(t('history.err_clear_payment'));
    }
  };

  const handleDeleteVisit = async (visitId) => {
    if (!window.confirm(t('history.confirm_delete_visit'))) return;
    try {
      await deleteClientVisit(visitId);
      setHistory(history.filter(h => h.id !== visitId));
    } catch {
      alert(t('history.err_delete_visit'));
    }
  };

  const handleAddVisit = async () => {
    setAddingVisit(true);
    try {
      const { data } = await addManualVisit(client.id, {
        visit_date: visitForm.visit_date,
        amount_charged: parseFloat(visitForm.amount_charged) || 0,
        amount_paid: parseFloat(visitForm.amount_paid) || 0,
        tip: parseFloat(visitForm.tip) || 0,
        payment_notes: visitForm.payment_notes,
        completed: visitForm.completed,
        paid: visitForm.paid
      });
      setHistory([data, ...history]);
      setShowAddVisit(false);
      setVisitForm({
        visit_date: getLocalDate(),
        amount_charged: client.recurring_amount || '',
        amount_paid: '',
        tip: '',
        payment_notes: '',
        completed: true,
        paid: false
      });
    } catch {
      alert(t('history.err_add_visit'));
    }
    setAddingVisit(false);
  };

  const totalCuts = history.length;
  const totalCharged = history.reduce((s, h) => s + (parseFloat(h.amount_charged) || 0), 0);
  const totalCollected = history.reduce((s, h) => s + (parseFloat(h.amount_paid) || 0) + (parseFloat(h.tip) || 0), 0);
  const totalOwed = totalCharged - totalCollected;
  const unpaidCuts = history.filter(h => !h.paid).length;

  const filtered = filter === 'all' ? history :
    filter === 'paid' ? history.filter(h => h.paid) :
    history.filter(h => !h.paid);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const locale = lang === 'es' ? 'es-MX' : 'en-US';
    return new Date(dateStr + 'T12:00:00').toLocaleDateString(locale, {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const fmt = (n) => `$${parseFloat(n || 0).toFixed(2)}`;

  const getVisitDate = (visit) => {
    return visit.routes?.route_date || null;
  };

  const getSlotLabel = (visit) => {
    const slot = visit.routes?.time_slot || visit.time_slot;
    if (slot === 'manual') return t('history.slot_manual');
    if (slot === 'morning') return t('history.slot_morning');
    if (slot === 'afternoon') return t('history.slot_afternoon');
    if (slot === 'evening') return t('history.slot_evening');
    return slot || '';
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && onClose) onClose();
  };

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div
        ref={modalRef}
        style={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <style>{historyCSS}</style>
        <div style={styles.modalHeader}>
          <div>
            <div id={titleId} style={styles.modalTitle}>
              {t('history.dialog_title', { name: client.name })}
            </div>
            <div style={styles.modalSub}>{client.street}, {client.city}</div>
          </div>
          <button
            ref={closeBtnRef}
            style={styles.closeBtn}
            onClick={onClose}
            aria-label={t('a11y.close_dialog')}
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>

        <div style={styles.stats}>
          <div style={styles.stat}>
            <div style={styles.statValue}>{totalCuts}</div>
            <div style={styles.statLabel}>{t('history.total_cuts')}</div>
          </div>
          <div style={styles.stat}>
            <div style={{ ...styles.statValue, color: '#14653a' }}>{fmt(totalCollected)}</div>
            <div style={styles.statLabel}>{t('history.collected')}</div>
          </div>
          <div style={styles.stat}>
            <div style={{ ...styles.statValue, color: totalOwed > 0 ? '#dc2626' : '#14653a' }}>
              {fmt(totalOwed)}
            </div>
            <div style={styles.statLabel}>{t('history.outstanding')}</div>
          </div>
        </div>

        {unpaidCuts > 0 && (
          <div style={styles.alertBox} role="status" aria-live="polite">
            <span aria-hidden="true">⚠️ </span>
            {unpaidCuts === 1
              ? t('history.unpaid_one', { n: unpaidCuts, amount: fmt(totalOwed) })
              : t('history.unpaid_many', { n: unpaidCuts, amount: fmt(totalOwed) })}
          </div>
        )}

        <div style={styles.topActions}>
          <div style={styles.filterRow} role="group" aria-label={t('history.filter_all')}>
            {[
              { key: 'all', label: t('history.filter_all') },
              { key: 'paid', label: t('history.filter_paid') },
              { key: 'unpaid', label: t('history.filter_unpaid') },
            ].map(({ key, label }) => (
              <button
                key={key}
                style={{ ...styles.filterBtn, ...(filter === key ? styles.filterActive : {}) }}
                onClick={() => setFilter(key)}
                aria-pressed={filter === key}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            className="ms-history-addbtn"
            style={styles.addVisitBtn}
            onClick={() => setShowAddVisit(!showAddVisit)}
            aria-expanded={showAddVisit}
          >
            {t('history.add_visit')}
          </button>
        </div>

        {showAddVisit && (
          <div style={styles.addVisitForm} role="region" aria-labelledby={addTitleId}>
            <div id={addTitleId} style={styles.addVisitTitle}>{t('history.add_visit_title')}</div>
            <div style={styles.formRow}>
              <div style={styles.formField}>
                <label htmlFor={visitDateId} style={styles.formLabel}>{t('history.visit_date')}</label>
                <input
                  id={visitDateId}
                  type="date"
                  style={styles.formInput}
                  value={visitForm.visit_date}
                  onChange={e => setVisitForm({ ...visitForm, visit_date: e.target.value })}
                />
              </div>
              <div style={styles.formField}>
                <label htmlFor={chargedId} style={styles.formLabel}>{t('history.amount_charged')}</label>
                <input
                  id={chargedId}
                  type="number"
                  style={styles.formInput}
                  placeholder="0.00"
                  value={visitForm.amount_charged}
                  onChange={e => setVisitForm({ ...visitForm, amount_charged: e.target.value })}
                />
              </div>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formField}>
                <label htmlFor={paidId} style={styles.formLabel}>{t('history.amount_paid')}</label>
                <input
                  id={paidId}
                  type="number"
                  style={styles.formInput}
                  placeholder="0.00"
                  value={visitForm.amount_paid}
                  onChange={e => setVisitForm({ ...visitForm, amount_paid: e.target.value })}
                />
              </div>
              <div style={styles.formField}>
                <label htmlFor={tipId} style={styles.formLabel}>{t('history.tip')}</label>
                <input
                  id={tipId}
                  type="number"
                  style={styles.formInput}
                  placeholder="0.00"
                  value={visitForm.tip}
                  onChange={e => setVisitForm({ ...visitForm, tip: e.target.value })}
                />
              </div>
            </div>
            <div style={styles.formField}>
              <label htmlFor={notesId} style={styles.formLabel}>{t('history.notes')}</label>
              <input
                id={notesId}
                type="text"
                style={styles.formInput}
                placeholder={t('history.notes_placeholder')}
                value={visitForm.payment_notes}
                onChange={e => setVisitForm({ ...visitForm, payment_notes: e.target.value })}
              />
            </div>
            <div style={styles.formCheckRow}>
              <label htmlFor={completedId} style={styles.checkLabel}>
                <input
                  id={completedId}
                  type="checkbox"
                  checked={visitForm.completed}
                  onChange={e => setVisitForm({ ...visitForm, completed: e.target.checked })}
                />
                {' '}{t('history.completed')}
              </label>
              <label htmlFor={paidCheckId} style={styles.checkLabel}>
                <input
                  id={paidCheckId}
                  type="checkbox"
                  checked={visitForm.paid}
                  onChange={e => setVisitForm({ ...visitForm, paid: e.target.checked })}
                />
                {' '}{t('history.paid')}
              </label>
            </div>
            <div style={styles.formActions}>
              <button style={styles.cancelBtn} onClick={() => setShowAddVisit(false)}>
                {t('history.cancel')}
              </button>
              <button
                className="ms-history-savebtn"
                style={styles.saveBtn}
                onClick={handleAddVisit}
                disabled={addingVisit}
              >
                {addingVisit ? t('history.saving') : t('history.save_visit')}
              </button>
            </div>
          </div>
        )}

        {loading && (
          <div style={styles.loading} role="status" aria-live="polite">{t('history.loading')}</div>
        )}
        {!loading && filtered.length === 0 && (
          <div style={styles.empty}>{t('history.empty')}</div>
        )}

        {!loading && filtered.map((visit, index) => {
          const charged = parseFloat(visit.amount_charged) || 0;
          const paid = parseFloat(visit.amount_paid) || 0;
          const tip = parseFloat(visit.tip) || 0;
          const balance = charged - paid - tip;
          const visitDate = getVisitDate(visit);
          const slotLabel = getSlotLabel(visit);

          return (
            <div key={visit.id} style={styles.visitCard}>
              <div style={styles.visitTop}>
                <div style={styles.visitLeft}>
                  <div style={styles.visitNum} aria-hidden="true">#{totalCuts - index}</div>
                  <div>
                    <div style={styles.visitDate}>{formatDate(visitDate)}</div>
                    <div style={styles.slotLabel}>{slotLabel}</div>
                    <div style={styles.visitStatusRow}>
                      <span style={{
                        ...styles.statusBadge,
                        background: visit.completed ? '#EAF3DE' : '#f0f0f0',
                        color: visit.completed ? '#14653a' : '#5f6b58'
                      }}>
                        {visit.completed ? t('history.status_completed') : t('history.status_pending')}
                      </span>
                      <span style={{
                        ...styles.statusBadge,
                        background: visit.paid ? '#EAF3DE' : '#FEF2F2',
                        color: visit.paid ? '#14653a' : '#dc2626'
                      }}>
                        {visit.paid ? t('history.status_paid') : t('history.status_unpaid')}
                      </span>
                    </div>
                  </div>
                </div>
                <div style={styles.visitRight}>
                  {charged > 0 && (
                    <button
                      style={styles.iconBtn}
                      onClick={() => handleDeletePayment(visit.id)}
                      aria-label={t('a11y.clear_payment')}
                      title={t('a11y.clear_payment')}
                    >
                      <span aria-hidden="true">💰</span>
                    </button>
                  )}
                  <button
                    style={styles.iconBtn}
                    onClick={() => handleDeleteVisit(visit.id)}
                    aria-label={t('a11y.delete_visit')}
                    title={t('a11y.delete_visit')}
                  >
                    <span aria-hidden="true">🗑</span>
                  </button>
                </div>
              </div>

              <div style={styles.actionRow}>
                <button
                  style={{
                    ...styles.smallBtn,
                    background: visit.completed ? '#f0f0f0' : '#EAF3DE',
                    color: visit.completed ? '#5f6b58' : '#14653a',
                    border: visit.completed ? '1px solid #ddd' : '1px solid #14653a'
                  }}
                  onClick={() => handleToggleComplete(visit)}
                  disabled={markingComplete === visit.id}
                >
                  {markingComplete === visit.id
                    ? '...'
                    : visit.completed
                      ? t('history.mark_incomplete')
                      : t('history.mark_complete')}
                </button>
                {!visit.paid && (
                  <button
                    className="ms-history-smallpay"
                    style={styles.smallBtnPay}
                    onClick={() => handleMarkPaid(visit)}
                    disabled={markingPaid === visit.id}
                  >
                    {markingPaid === visit.id ? '...' : t('history.mark_paid')}
                  </button>
                )}
              </div>

              {charged > 0 && (
                <div style={styles.paymentDetails}>
                  <div style={styles.payRow}>
                    <span style={styles.payLabel}>{t('history.charged')}</span>
                    <span style={styles.payValue}>{fmt(charged)}</span>
                  </div>
                  {paid > 0 && (
                    <div style={styles.payRow}>
                      <span style={styles.payLabel}>{t('history.paid_label')}</span>
                      <span style={{ ...styles.payValue, color: '#14653a' }}>{fmt(paid)}</span>
                    </div>
                  )}
                  {tip > 0 && (
                    <div style={styles.payRow}>
                      <span style={styles.payLabel}>{t('history.tip_label')}</span>
                      <span style={{ ...styles.payValue, color: '#14653a' }}>{fmt(tip)}</span>
                    </div>
                  )}
                  {balance > 0 && (
                    <div style={styles.payRow}>
                      <span style={styles.payLabel}>{t('history.balance_due')}</span>
                      <span style={{ ...styles.payValue, color: '#dc2626' }}>{fmt(balance)}</span>
                    </div>
                  )}
                  {visit.payment_notes && (
                    <div style={styles.payNotes}>{visit.payment_notes}</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 12, padding: 20, width: '100%', maxWidth: 420, margin: '0 16px', maxHeight: '90vh', overflowY: 'auto' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  modalTitle: { fontSize: 16, fontWeight: 500, color: '#1a1a1a' },
  modalSub: { fontSize: 12, color: '#5f6b58', marginTop: 2 },
  closeBtn: { border: 'none', background: 'none', fontSize: 16, cursor: 'pointer', color: '#5f6b58', padding: 4, borderRadius: 6 },
  stats: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 },
  stat: { background: '#f9f9f9', borderRadius: 8, padding: '10px 8px', textAlign: 'center' },
  statValue: { fontSize: 18, fontWeight: 500, color: '#1a1a1a' },
  statLabel: { fontSize: 11, color: '#5f6b58', marginTop: 2 },
  alertBox: { background: '#FEF2F2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 },
  topActions: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  filterRow: { display: 'flex', gap: 6 },
  filterBtn: { padding: '4px 12px', border: '1px solid #ddd', background: 'none', borderRadius: 20, fontSize: 12, cursor: 'pointer', color: '#5f6b58' },
  filterActive: { background: '#EAF3DE', color: '#14653a', borderColor: '#14653a' },
  addVisitBtn: { padding: '5px 12px', border: 'none', background: '#14653a', borderRadius: 20, fontSize: 12, fontWeight: 500, color: '#fff', cursor: 'pointer' },
  addVisitForm: { background: '#f9f9f9', borderRadius: 10, padding: 14, marginBottom: 12, border: '1px solid #eee' },
  addVisitTitle: { fontSize: 13, fontWeight: 500, color: '#1a1a1a', marginBottom: 10 },
  formRow: { display: 'flex', gap: 8, marginBottom: 8 },
  formField: { flex: 1 },
  formLabel: { display: 'block', fontSize: 11, color: '#5f6b58', marginBottom: 3 },
  formInput: { width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' },
  formCheckRow: { display: 'flex', gap: 16, marginBottom: 10, marginTop: 4 },
  checkLabel: { fontSize: 13, color: '#1a1a1a', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 },
  formActions: { display: 'flex', gap: 8, justifyContent: 'flex-end' },
  loading: { textAlign: 'center', padding: 24, color: '#5f6b58' },
  empty: { textAlign: 'center', padding: 24, color: '#5f6b58', fontSize: 14 },
  visitCard: { border: '1px solid #eee', borderRadius: 10, padding: '10px 12px', marginBottom: 8 },
  visitTop: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 },
  visitLeft: { display: 'flex', alignItems: 'flex-start', gap: 10 },
  visitRight: { display: 'flex', alignItems: 'center', gap: 4 },
  visitNum: { width: 28, height: 28, borderRadius: '50%', background: '#EAF3DE', color: '#14653a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 500, flexShrink: 0, marginTop: 2 },
  visitDate: { fontSize: 13, fontWeight: 500, color: '#1a1a1a', marginBottom: 2 },
  slotLabel: { fontSize: 11, color: '#5f6b58', marginBottom: 4 },
  visitStatusRow: { display: 'flex', gap: 4, flexWrap: 'wrap' },
  statusBadge: { fontSize: 10, padding: '2px 7px', borderRadius: 20, fontWeight: 500 },
  iconBtn: { border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, padding: 6, borderRadius: 6, minWidth: 32, minHeight: 32 },
  actionRow: { display: 'flex', gap: 6, marginBottom: 8 },
  smallBtn: { padding: '5px 10px', borderRadius: 20, fontSize: 11, cursor: 'pointer', fontWeight: 500 },
  smallBtnPay: { padding: '5px 10px', border: 'none', background: '#14653a', borderRadius: 20, fontSize: 11, fontWeight: 500, color: '#fff', cursor: 'pointer' },
  paymentDetails: { borderTop: '1px solid #f0f0f0', paddingTop: 8 },
  payRow: { display: 'flex', justifyContent: 'space-between', marginBottom: 4 },
  payLabel: { fontSize: 12, color: '#5f6b58' },
  payValue: { fontSize: 12, fontWeight: 500, color: '#1a1a1a' },
  payNotes: { fontSize: 11, color: '#5f6b58', fontStyle: 'italic', marginTop: 4 },
  cancelBtn: { padding: '6px 14px', border: '1px solid #ddd', background: 'none', borderRadius: 8, fontSize: 12, cursor: 'pointer', color: '#1a1a1a' },
  saveBtn: { padding: '6px 14px', border: 'none', background: '#14653a', borderRadius: 8, fontSize: 12, fontWeight: 500, color: '#fff', cursor: 'pointer' }
};
